<template>
    <div>
        <b-modal
            id="termsModal"
            ref="termsModal"
            :lazy="true"
            title-tag="h2"
            hide-footer
            title="Thanks for choosing us"
        >
            <p>
                <big
                    >Hi, we're {{ SYSTEM_COMPANY_NAME }}. We provide products
                    and services that help animal healthcare providers run their
                    businesses better, including cloud practice management
                    software, and related products and services (together, our
                    "Services"). This document explains the terms that govern
                    your use of our Services. You should also read our
                    <a @click.prevent="policyClick" class="link" href="#"
                        >privacy policy</a
                    >, which describes how we collect and use information about
                    you. By using our Services you agree to be bound by these
                    terms and our privacy policy, so please read them carefully.
                    These terms were last updated on February 1, 2019.</big
                >
            </p>

            <p>
                <b>Using Our Services.</b> If you're using our Services on
                behalf of a business, then you are agreeing to these terms on
                behalf of that business, and you represent and warrant that you
                have the legal authority to bind that business (when we refer to
                "you" in this document, we're referring to you and that
                business). Please use our Services responsibly and according to
                these terms. Don't try to undermine the security, integrity or
                availability of our Services. Don't sell, or lease our Services,
                reverse engineer our Services, allow our competitors to access
                our Services, or attempt to create a similar product or service
                based on our Services. You may only use our Services as
                permitted by these terms, according to any written agreement
                between us, and by applicable laws, including applicable privacy
                laws. You are responsible for obtaining and maintaining all
                consents required for you to use our Services, including any
                consents from individuals whose data you might store in our
                Services (such as your clients). We may suspend providing our
                Services to you, or terminate your account, if we suspect that
                you violated these terms. We are dedicated to continually
                improving our Services. To that end, we regularly change our
                Services, sometimes adding or removing functionality.
            </p>

            <p>
                <b>Accounts and Administrators.</b> You will need an account to
                use our Services. Your account may be assigned to you by an
                administrator, who will also be able to access or disable your
                account. You are responsible for keeping your account
                information accurate, safeguarding your password, and for
                actions taken using your account. If you learn of, or suspect,
                any unauthorized use of your account, please contact us right
                away.
            </p>

            <p>
                <b>Content.</b> Our Services allow you to upload and store
                content. You remain the owner of that content, and are solely
                responsible for it. In order to operate and improve our
                Services, you grant us a worldwide, limited term license to
                store, process, copy, use, and display your content to you and
                other users of your account. This includes the right to copy
                your content to our databases, make backups, and analyze it on
                our servers. To the extent any of that use constitutes a
                modification or derivative work, then you also give us a license
                to make modifications and derivative works of your content. Make
                sure you have the necessary rights to grant us this license. We
                may not monitor content that is submitted or uploaded to our
                Services, but we may remove any content that we believe violates
                these terms. We may also access your account in order to respond
                to your support requests. This license does not grant us the
                right to sell your content.
            </p>

            <p>
                <b>Protecting Your Content.</b> We take reasonable measures to
                safeguard the security, confidentiality and integrity of our
                Services and your content, but we cannot guarantee that
                security, confidentiality or integrity. While we regularly
                backup our Services and your content, we recommend that you also
                keep copies of your content.
            </p>

            <p>
                <b>Third Party Products, Services, and Content.</b> Our Services
                allow you to access and use third party products or services
                that we do not own or control. If you choose to access or use
                these third party products or services, you do so at your own
                risk, and you allow us to share your information and content
                with these third parties to enable them to provide the products,
                or perform the services, you requested. We do not endorse,
                support or warrant these products or services, and you agree to
                not hold us responsible for any loss or damage that you may
                suffer as a result of accessing or using these products or
                services. Our Services may also include content that is not
                ours, or that was uploaded or submitted to our Services by third
                parties. If you use or rely on this content, you do so at your
                own risk.
            </p>

            <p>
                <b>Our Property and Your Feedback.</b> Using our Services does
                not give you ownership of, or any right to (other than the right
                to use described in these terms), any of our or our licensors'
                intellectual property. Your feedback helps us to improve our
                Services and to develop new products and services. We are
                grateful for any feedback you choose to provide us, and we may
                freely use it without any obligation.
            </p>

            <p>
                <b>Payments.</b> All payments are due in U.S. Dollars. Unless
                otherwise agreed by both of us in writing, we will charge your
                credit card on a monthly basis starting on the day you make your
                purchase. We will continue to charge your credit card until your
                subscription to our Services expires or is terminated pursuant
                to these terms. Our fees are exclusive of all taxes, and you are
                responsible for paying any tax applicable to your purchase. If
                your credit card is declined (or you otherwise fail to pay us),
                we may suspend your access to our Services, but we will notify
                you before we do.
            </p>

            <p>
                <b>Terminating Services.</b> You may terminate your subscription
                to our Services at any time by providing us with written notice
                (email is fine); provided, that in that event: (a) we will not
                refund any fees that you prepaid prior to the termination, and
                (b) all contractually committed fees will become immediately due
                and payable to us. We may also stop providing our Services to
                you at any time, in which case we will give you reasonable
                advance notice and a pro-rata refund of any fees you prepaid.
                You must immediately cease using our Services when your access
                to our Service expires or is terminated for any reason: You may
                export your content from our Services in comma-separated value
                (CSV) format using the reports available in our Services. If
                your prefer that we provide your content in Structured Query
                Language (SQL) format, we will do so twice at no charge. These
                terms will automatically terminate when your access to our
                Service expires or is terminated for any reason, except with
                respect to outstanding payment obligations and all obligation of
                either that by their nature would continue beyond the
                termination, cancellation or expiration of these terms.
                Following termination or expiration of your subscription to our
                Service, we will have no obligation to maintain any of your
                content and, unless legally prohibited, we will delete it within
                thirty (30) calendar days after such termination or expiration.
            </p>

            <p>
                <b>Disclaimer.</b> OUR SERVICES ARE PROVIDED "AS IS" AND "AS
                AVAILABLE." TO THE FULLEST EXTENT PERMITTED BY LAW, EXCEPT AS
                EXPRESSLY PROVIDED IN THESE TERMS, WE DON'T MAKE ANY WARRANTY
                ABOUT OUR SERVICES, EXPRESS, IMPLIED, STATUTORY OR OTHERWISE,
                INCLUDING WITHOUT LIMITATION, ANY IMPLIED WARRANTY OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
                NON-INFRINGEMENT.
            </p>

            <p>
                <b>Indemnification.</b> You agree to indemnify and hold us (and
                our employees, officers, directors and affiliates) harmless from
                and against any and all claims, damages, obligations, losses,
                liabilities, costs and expenses (including reasonable attorney's
                fees) arising out of a third party claim made or brought against
                us relating to: (i) your use of our Services in violation of
                these terms, (ii) any of your content, or (iii) any third
                party's use of our Services with your account.
            </p>

            <p>
                <b>Infringement Claims.</b> If we believe our Services might
                infringe a third party's intellectual property rights, then we
                will, at our expense: (i) obtain the right for you to continue
                using our Services, (ii) provide replacement functionality that
                is non-infringing, or (iii) modify our Services so that they are
                no longer infringing. If we believe that none of these options
                are commercially reasonable, then we may terminate your access
                to our Services and provide you with a pro-rata refund of any
                prepaid fees after the date of termination. These remedies are
                your exclusive remedies, and our exclusive obligation, with
                respect to infringement claims.
            </p>

            <p>
                <b>Liability for Our Services.</b> TO THE FULLEST EXTENT
                PERMITTED BY LAW, (I) WE WILL NOT BE RESPONSIBLE FOR ANY LOST
                PROFITS, REVENUES, DATA, LOSSES OR INDIRECT, SPECIAL,
                CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES, EVEN IF WE KNEW OR
                SHOULD HAVE KNOWN THAT SUCH DAMAGES WERE POSSIBLE, AND (II) OUR
                TOTAL LIABILITY FOR ANY CLAIM RELATING TO THESE TERMS IS LIMITED
                TO THE AMOUNT YOU PAID TO US TO USE OUR SERVICES DURING THE 12
                MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY.
            </p>

            <p>
                <b>Support.</b> If you experience a technical issue while using
                our Services that you are unable to resolve yourself, you may
                email us at support@vettersoftware.com or call us at +1 (844)
                4-VETTER. We will respond to your request promptly.
            </p>

            <p>
                <b>Publicity.</b> We're proud that you're our client, and we
                want the world to know it. You permit us to use your name and
                logo in our customer lists, promotional materials and in
                conversations. You do not give up any ownership rights in your
                name or logo, and if you no longer want us to use your name or
                logo in the future, please send us an email to let us know.
            </p>

            <p>
                <b>Miscellaneous.</b> We may modify these terms at any time, so
                you should review these terms regularly. Changes will be
                effective immediately, but they will not apply retroactively. If
                you do not agree to the modified terms, you must stop using our
                Services. These terms govern our relationship with you - they do
                not create any third party beneficiary rights. Our failure to
                enforce any right does not mean that we are giving up any of our
                rights. Neither of us will be liable for a failure to perform
                our obligations (excluding obligations to pay fees) as a result
                of a condition that is outside of our reasonable control (such
                as a natural disaster, war or Internet disturbance). These terms
                (including our privacy policy) make up the entire agreement
                between us relating to our Services and your use of our
                Services. If a court decides that a portion of these terms is
                not enforceable, the rest of these terms will not be affected.
                These terms are governed by the laws of the State of California,
                excluding its conflict of laws rules. If there is a dispute
                between us, both of us agree to work together in good faith to
                try to resolve that dispute before resorting to taking any legal
                action. All disputes relating to these terms or our Services be
                litigated exclusively in the federal or state courts of San
                Mateo County, California, and we both consent to personal
                jurisdiction in those courts.
            </p>
        </b-modal>
    </div>
</template>
<style>
.link {
    font-weight: normal !important;
    color: #0088cc !important;
    text-decoration: none !important;
}
.link:hover {
    font-weight: normal !important;
    color: #005580 !important;
    text-decoration: underline !important;
}
</style>
<script>
export default {
    data() {
        return {};
    },
    mounted() {},
    methods: {
        open() {
            this.$refs.termsModal.show();
        },
        policyClick() {
            this.$refs.termsModal.hide();
            this.$root.$emit("privacyPolicyModal");
        }
    }
};
</script>
